import { SynChip } from './SynChip';
import { Commentable } from '@/shared/comments/Commentable';
import { vizToneColor } from '@/versions/v1/modules/_viz';
import type { GmRollupView } from './RollupStrip.transform';

/**
 * The GM roll-up band (#169, A §M4): the same facts compressed to a handful of
 * 24 h / WTD / MTD numbers vs target, one band on every route that shows it
 * (the Plant model's GM lead band above both views, `/metal-accounting`'s
 * summary band for the GM and Superintendent). One hairline cell per metric:
 * the lead window's figure in its state tone, the named comparator, then the
 * other windows; the bottleneck cell carries the dashed [syn] chip.
 *
 * The band sizes to the cells it is given (the comp's `.band{--n}`): four on
 * the Plant model (three metrics + bottleneck), two on `/metal-accounting`,
 * whose strip already prints the recovery figures and so drops those metrics
 * (komatsu.F11 / #737).
 */
const BAND_COLS: Readonly<Record<number, string>> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

export function RollupStrip({ view }: { view: GmRollupView }) {
  const cellCount = view.metrics.length + 1;
  return (
    <Commentable name="RollupStrip">
      <section
        aria-label="Roll-up"
        data-rollup-band
        data-gm-band
        data-gm-cells={cellCount}
        className={`grid grid-cols-2 overflow-hidden rounded-2xl border border-[var(--hairline)] bg-[var(--surface-2)] tabular-nums ${BAND_COLS[cellCount] ?? 'lg:grid-cols-4'}`}
      >
        {view.metrics.map((metric) => {
          const [lead, ...rest] = metric.cells;
          return (
            <div
              key={metric.id}
              data-rollup-cell={metric.id}
              data-gm-metric={metric.id}
              title={metric.title}
              className="min-w-0 border-b border-r border-[var(--hairline)] px-4 py-2.5 lg:border-b-0"
            >
              <span className="block text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
                {metric.label} · {lead?.window}
              </span>
              <span
                className="mt-0.5 block text-2xl font-semibold tracking-tight text-ink-primary"
                style={lead && lead.tone !== 'neutral' ? { color: vizToneColor(lead.tone) } : undefined}
                data-gm-value
              >
                {lead?.value}
              </span>
              <span className="block text-xs text-muted">{metric.target}</span>
              <span className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-muted">
                {rest.map((cell) => (
                  <span key={cell.window} className="whitespace-nowrap">
                    {cell.window} <span className="font-semibold text-ink-secondary">{cell.value}</span>
                  </span>
                ))}
              </span>
            </div>
          );
        })}
        <div
          className="min-w-0 px-4 py-2.5"
          title={view.bottleneck.title}
          data-rollup-cell="bottleneck"
          data-gm-metric="bottleneck"
        >
          <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
            Bottleneck
            <SynChip title={view.synTitle} />
          </span>
          <span
            className="mt-1 block break-words text-[15px] font-semibold leading-snug text-ink-primary"
            style={view.bottleneck.tone === 'neutral' ? undefined : { color: vizToneColor(view.bottleneck.tone) }}
          >
            {view.bottleneck.label}
          </span>
          <span className="mt-0.5 block text-xs text-muted" data-gm-bottleneck-sub>
            {view.bottleneck.sub}
          </span>
        </div>
      </section>
    </Commentable>
  );
}
